export type BadgeVariant = 'primary' | 'secondary';

export function translateTemporarySource(sourceType: string | null | undefined): '一時' | '手動' {
  if (!sourceType) return '一時';
  const normalized = sourceType.toLowerCase();
  if (normalized === 'upload' || normalized === 'manual') return '手動';
  return '一時';
}

export function translateCloudSource(sourceType: string | null | undefined): 'クラウド' | '手動' {
  if (!sourceType) return 'クラウド';
  const normalized = sourceType.toLowerCase();
  if (normalized === 'manual' || normalized === 'upload') return '手動';
  return 'クラウド';
}

export type InfoBadge = {
  label: string;
  variant?: BadgeVariant;
  /** ホバーで出す補足 (title 属性)。レイヤー素材の解析時の warnings 等 */
  title?: string;
};

/** MIMEタイプを人間に読みやすい形式名に変換 (例: "image/jpeg" → "JPEG") */
export function formatContentType(contentType?: string | null): string | null {
  if (!contentType) return null;
  const map: Record<string, string> = {
    'image/jpeg': 'JPEG',
    'image/jpg': 'JPEG',
    'image/png': 'PNG',
    'image/gif': 'GIF',
    'image/webp': 'WebP',
    'image/svg+xml': 'SVG',
    'image/bmp': 'BMP',
    'image/tiff': 'TIFF',
    'image/avif': 'AVIF',
    'image/heic': 'HEIC',
    'image/heif': 'HEIF',
    'video/mp4': 'MP4',
    'video/webm': 'WebM',
    'video/quicktime': 'MOV',
    'video/x-msvideo': 'AVI',
    'video/x-matroska': 'MKV',
    'video/mpeg': 'MPEG',
  };
  const lower = contentType.toLowerCase();
  if (map[lower]) return map[lower];
  // フォールバック: サブタイプを大文字で返す
  const sub = lower.split('/')[1];
  return sub ? sub.toUpperCase() : null;
}

/** SVG 素材か (レイヤー素材は本体が合成 PNG なので対象外) */
export function isSvgContentType(contentType?: string | null): boolean {
  return (contentType || '').split(';')[0].trim().toLowerCase() === 'image/svg+xml';
}

/**
 * 非ラスタ原本 (そのままでは生成 AI・案件画像などラスタ画像しか受け付けない経路に渡せない形式) か。
 * BE の判定 (services/materials/renditions.py の描画器レジストリ) と揃える。現状は SVG のみ。
 * 将来 PSD を足すときはここに追加する (docs/features/material-raster-rendition-plan.md §3)。
 */
export function isNonRasterOriginal(contentType?: string | null): boolean {
  return isSvgContentType(contentType);
}

/** ラスタ版の描画に失敗した非ラスタ原本に出す控えめなバッジ (ホバーで補足) */
export const RASTER_FAILED_BADGE_LABEL = '画像化に失敗';

export function createRasterStatusBadges(material: { content_type?: string | null; raster_status?: string | null }): InfoBadge[] {
  if (!isNonRasterOriginal(material.content_type) || material.raster_status !== 'failed') return [];
  return [{
    label: RASTER_FAILED_BADGE_LABEL,
    variant: 'secondary',
    title: 'この素材は画像 (PNG) に変換できませんでした。AI 生成や案件画像には使えません',
  }];
}

/** 容量バッジ: 1MB 未満は KB で出す (SVG 等の小さいファイルが「0.0MB」にならないように) */
export function formatFileSizeBadge(bytes?: number | null): string | null {
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return null;
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)}MB`;
  const kb = bytes / 1024;
  return kb >= 100 ? `${Math.round(kb)}KB` : `${kb.toFixed(1)}KB`;
}

/** 詳細表示用の容量: 1MB 以上は「2.35 MB」、未満は「2.3 KB」。不明は「-」 */
export function formatFileSizeLabel(bytes?: number | null): string {
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return '-';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(2)} MB` : `${(bytes / 1024).toFixed(1)} KB`;
}

/** SVG 素材のバッジ: 形式名「SVG」と容量 (幅・高さはベクターなので出さない) */
export function createSvgBadgeLabels(material: { content_type?: string | null; file_size_bytes?: number | null }): string[] {
  if (!isSvgContentType(material.content_type)) return [];
  const sizeLabel = formatFileSizeBadge(material.file_size_bytes);
  return sizeLabel ? ['SVG', sizeLabel] : ['SVG'];
}

export function normalizeFileType(contentType?: string | null): 'image' | 'video' | 'audio' | null {
  if (!contentType) return null;
  if (contentType.startsWith('video/')) return 'video';
  if (contentType.startsWith('image/')) return 'image';
  if (contentType.startsWith('audio/')) return 'audio';
  return null;
}

export function formatDurationBadge(durationSeconds?: number | string | null): string | null {
  if (durationSeconds === null || durationSeconds === undefined) {
    return null;
  }
  const numeric = typeof durationSeconds === 'string' ? Number.parseFloat(durationSeconds) : durationSeconds;
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null;
  }
  return `${Math.round(numeric)}秒`;
}

export function createInfoBadges(labels: Array<string | null | undefined>, secondaryLabels?: string[]): InfoBadge[] {
  const secondarySet = new Set(secondaryLabels || []);
  return labels
    .filter((label): label is string => Boolean(label && label.trim()))
    .map((label) => ({
      label,
      variant: secondarySet.has(label) ? 'secondary' : 'primary'
    }));
}

/**
 * Seedance 2.0 が出力する 480p の非標準解像度 → 標準比率のホワイトリスト。
 * Seedance の 480p は 16:9/9:16 を指定しても標準解像度に丸まらず 864x496 / 496x864 で
 * 出力される。GCD 正規化すると 54:31 等になり (比率 1.74 はむしろ 7:4 寄り)、
 * 通常の標準比率判定・許容誤差判定では拾えないため、実寸でホワイトリスト判定する。
 */
const SEEDANCE_NONSTANDARD_RATIOS: Record<string, '16:9' | '9:16'> = {
  '864x496': '16:9',
  '496x864': '9:16',
};

/**
 * Calculate aspect ratio from width and height
 * Returns only standard aspect ratios: 16:9, 9:16, 1:1, 7:4, 4:7, 1.77:1, 1:1.77
 * Returns null for non-standard ratios
 */
export function calculateAspectRatio(width?: number | null, height?: number | null): string | null {
  if (!width || !height || width <= 0 || height <= 0) {
    return null;
  }

  // Check for square (1:1)
  if (width === height) {
    return '1:1';
  }

  // Seedance 2.0 の 480p 非標準解像度を標準比率として扱う (規格外にしない)
  const seedanceRatio = SEEDANCE_NONSTANDARD_RATIOS[`${width}x${height}`];
  if (seedanceRatio) {
    return seedanceRatio;
  }

  // Calculate exact aspect ratio using GCD
  const gcd = (a: number, b: number): number => {
    let x = Math.abs(a);
    let y = Math.abs(b);
    while (y !== 0) {
      const temp = x % y;
      x = y;
      y = temp;
    }
    return x;
  };

  const divisor = gcd(width, height) || 1;
  const normalizedWidth = Math.round(width / divisor);
  const normalizedHeight = Math.round(height / divisor);

  // Check for exact standard ratios first (before tolerance matching)
  if (normalizedWidth === 16 && normalizedHeight === 9) {
    return '16:9';
  }
  if (normalizedWidth === 9 && normalizedHeight === 16) {
    return '9:16';
  }
  if (normalizedWidth === 7 && normalizedHeight === 4) {
    return '7:4';
  }
  if (normalizedWidth === 4 && normalizedHeight === 7) {
    return '4:7';
  }

  // Check for 1.77:1 format (ratios close to 1.77)
  const ratio = width / height;
  const tolerance = 0.05; // 5% tolerance

  // 1.77:1 (horizontal)
  const ratio177 = 1.77;
  if (Math.abs(ratio - ratio177) / ratio177 < tolerance) {
    return '1.77:1';
  }

  // 1:1.77 (vertical)
  const ratio1_177 = 1 / 1.77;
  if (Math.abs(ratio - ratio1_177) / ratio1_177 < tolerance) {
    return '1:1.77';
  }

  // Non-standard ratio - return null (no tag)
  return null;
}

/**
 * Check if aspect ratio is valid for video generation
 * Valid ratios: 16:9, 9:16, 7:4, 4:7
 */
export function isValidVideoGenerationAspectRatio(width?: number | null, height?: number | null): boolean {
  const aspectRatio = calculateAspectRatio(width, height);
  return aspectRatio === '16:9' || aspectRatio === '9:16' || aspectRatio === '7:4' || aspectRatio === '4:7';
}

export function getVideoResolutionLabel(width?: number | null, height?: number | null): string | null {
  if (!width || !height) {
    return '規格外';
  }
  if (width === height) {
    return '1:1';
  }
  const key = `${width}x${height}`;

  const horizontalLabels: Record<string, string> = {
    '640x360': 'SD',
    '854x480': 'SD',
    '864x496': '480p',  // Seedance 2.0 の 480p 16:9
    '1280x720': '720p',
    '1920x1080': '1080p',
    '2560x1440': '1440p',
    '3840x2160': '4K',
  };

  const verticalLabels: Record<string, string> = {
    '496x864': '480p縦型',  // Seedance 2.0 の 480p 9:16
    '720x1280': 'HD縦型',
    '1080x1920': 'Full HD縦型',
    '1440x2560': 'QHD縦型',
    '2160x3840': '4K縦型',
  };

  if (width > height && horizontalLabels[key]) {
    return horizontalLabels[key];
  }
  if (height > width && verticalLabels[key]) {
    return verticalLabels[key];
  }

  const gcd = (a: number, b: number): number => {
    let x = Math.abs(a);
    let y = Math.abs(b);
    while (y !== 0) {
      const temp = x % y;
      x = y;
      y = temp;
    }
    return x;
  };
  const divisor = gcd(width, height) || 1;
  const normalizedWidth = Math.round(width / divisor);
  const normalizedHeight = Math.round(height / divisor);

  if (normalizedWidth === 16 && normalizedHeight === 9) {
    return '規格外16:9';
  }
  if (normalizedWidth === 9 && normalizedHeight === 16) {
    return '規格外9:16';
  }

  return '規格外';
}
